import React from "react";
import { cn } from "../../lib/utils";
import { Spinner } from "./common";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "success"
    | "warning"
    | "info"
    | "ghost"
    | "link";
  size?: "sm" | "md" | "lg" | "icon";
  appearance?: "solid" | "outline" | "soft" | "text";
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
}

const AppButton: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "sm",
  appearance = "solid",
  isLoading = false,
  icon,
  iconPosition = "left",
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}) => {
  const baseStyles = cn(
    "relative inline-flex items-center justify-center rounded-md font-medium",
    "transition-colors duration-200",
    "focus:outline-none focus:ring-2 focus:ring-offset-2",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    fullWidth && "w-full"
  );

  const sizeStyles = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
    icon: "p-2",
  };

  const variants = {
    solid: {
      primary:
        "bg-primary text-white hover:bg-primary/90 focus:ring-primary/50",
      secondary: "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500",
      danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
      success:
        "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
      warning:
        "bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500",
      info: "bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500",
      ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
      link: "text-primary hover:underline focus:ring-primary/50",
    },
    outline: {
      primary:
        "border-2 border-primary text-primary hover:bg-primary hover:text-white focus:ring-primary/50",
      secondary:
        "border-2 border-gray-600 text-gray-600 hover:bg-gray-600 hover:text-white focus:ring-gray-500",
      danger:
        "border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white focus:ring-red-500",
      success:
        "border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white focus:ring-green-500",
      warning:
        "border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white focus:ring-yellow-500",
      info: "border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white focus:ring-blue-500",
      ghost:
        "border-2 border-gray-300 text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
      link: "text-primary hover:underline focus:ring-primary/50",
    },
    soft: {
      primary:
        "bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary/50",
      secondary:
        "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500",
      danger: "bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-500",
      success:
        "bg-green-50 text-green-600 hover:bg-green-100 focus:ring-green-500",
      warning:
        "bg-yellow-50 text-yellow-600 hover:bg-yellow-100 focus:ring-yellow-500",
      info: "bg-blue-50 text-blue-600 hover:bg-blue-100 focus:ring-blue-500",
      ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
      link: "text-primary hover:bg-primary/10 focus:ring-primary/50",
    },
    text: {
      primary: "text-primary hover:bg-primary/10 focus:ring-primary/50",
      secondary: "text-gray-600 hover:bg-gray-100 focus:ring-gray-500",
      danger: "text-red-600 hover:bg-red-50 focus:ring-red-500",
      success: "text-green-600 hover:bg-green-50 focus:ring-green-500",
      warning: "text-yellow-600 hover:bg-yellow-50 focus:ring-yellow-500",
      info: "text-blue-600 hover:bg-blue-50 focus:ring-blue-500",
      ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
      link: "text-primary hover:underline focus:ring-primary/50",
    },
  };

  const iconSpacing =
    size === "icon" ? "" : iconPosition === "left" ? "mr-2" : "ml-2";

  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={cn(
        baseStyles,
        sizeStyles[size],
        variants[appearance][variant],
        className
      )}
    >
      {isLoading && (
        <span className='"flex items-center"'>
          <Spinner />
        </span>
      )}
      <span className={cn(isLoading && "invisible", "flex items-center")}>
        {size === "icon" ? (
          icon || children
        ) : (
          <>
            {icon && iconPosition === "left" && (
              <span className={iconSpacing}>{icon}</span>
            )}
            {children}
            {icon && iconPosition === "right" && (
              <span className={iconSpacing}>{icon}</span>
            )}
          </>
        )}
      </span>
    </button>
  );
};

export default AppButton;
